import {
  getStorage,
  ref,
  uploadBytesResumable,
  getDownloadURL,
} from "firebase/storage";

const MAX_FILE_SIZES = {
  image: 5, // 5MB
  video: 20, // 20MB  
  audio: 20, // 20MB
};

const getAudioExtension = (mimeType = '') => {
  const normalized = mimeType.toLowerCase();
  if (normalized.includes('mp4') || normalized.includes('m4a')) return 'm4a';
  if (normalized.includes('mpeg') || normalized.includes('mp3')) return 'mp3';
  if (normalized.includes('wav')) return 'wav';
  return 'webm';
};

const uploadIncidentMedia = async (
  mediaFile,
  audioBlob,
  incidentId,
  basePath = `incidents/${incidentId}`,
  options = {}
) => {
  const storage = getStorage();
  const mediaUrls = [];
  
  try {
    // Upload photo/video file
    if (mediaFile?.file) {
      const fileSizeMB = mediaFile.file.size / (1024 * 1024);
      const maxSize = MAX_FILE_SIZES[mediaFile.type] || 5;
      
      if (fileSizeMB > maxSize) {
        throw new Error(`${mediaFile.type} file size exceeds ${maxSize}MB limit`);
      }
      
      const fileRef = ref(
        storage,
        `${basePath}/media/${options.imageObjectName || `${Date.now()}_${mediaFile.file.name}`}`
      );
      
      const uploadTask = uploadBytesResumable(fileRef, mediaFile.file);
      
      await new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(`Media upload progress: ${progress}%`);
          },
          (error) => {
            console.error('Media upload failed:', error);
            reject(error);
          },
          async () => {
            try {
              const url = await getDownloadURL(uploadTask.snapshot.ref);
              mediaUrls.push({ 
                url, 
                type: mediaFile.type,
                filename: mediaFile.file.name,
                path: uploadTask.snapshot.ref.fullPath,
                uploadedAt: new Date().toISOString()
              });
              resolve();
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    }
    
    // Upload audio blob
    if (audioBlob) {
      const audioSizeMB = audioBlob.size / (1024 * 1024);
      const audioExtension = getAudioExtension(audioBlob.type);
      
      if (audioSizeMB > MAX_FILE_SIZES.audio) {
        throw new Error(`Audio file size exceeds ${MAX_FILE_SIZES.audio}MB limit`);
      }
      
      const audioRef = ref(
        storage, 
        `${basePath}/audio/${options.audioObjectName || `${Date.now()}_recording.${audioExtension}`}`
      );
      
      const audioUploadTask = uploadBytesResumable(audioRef, audioBlob);
      
      await new Promise((resolve, reject) => {
        audioUploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log(`Audio upload progress: ${progress}%`);
          },
          (error) => {
            console.error('Audio upload failed:', error);
            reject(error);
          },
          async () => {
            try {
              const url = await getDownloadURL(audioUploadTask.snapshot.ref);
              mediaUrls.push({ 
                url, 
                type: 'audio',
                filename: `voice_recording.${audioExtension}`,
                path: audioUploadTask.snapshot.ref.fullPath,
                uploadedAt: new Date().toISOString()
              });
              resolve();
            } catch (error) {
              reject(error);
            }
          }
        );
      });
    }
    
    return mediaUrls;
  } catch (error) {
    console.error('Error uploading incident media:', error);
    throw error;
  }
};

export { uploadIncidentMedia };
